import type { ReactNode } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';

import inverterExample from '../../../assets/images/giv-energy-inverter.png';
import apiTokenExample from '../../../assets/images/giv-energy-manage-api-tokens.png';
import noExpiry from '../../../assets/images/giv-energy-no-expiry.png';
import tokenScopes from '../../../assets/images/giv-energy-token-scopes.png';
import appUsernameLocation from '../../../assets/images/growatt-username-location-app.png';
import usernameLocation from '../../../assets/images/growatt-username-location.png';
import serialNumberExample from '../../../assets/images/serial-number-example.png';
import SolisExampleOne from '../../../assets/images/solis-example-1.png';
import SolisExampleTwo from '../../../assets/images/solis-example-2.png';

export interface ConnectionField {
  name: string;
  label: string;
  type?: 'text' | 'password';
  placeholder?: string;
  validation?: Yup.AnySchema;
}

export interface InverterManufacturerConfig {
  /** Display name, sent to the API as `api_name` (upper-cased) and shown in headings. */
  manufacturer: string;
  fields: ConnectionField[];
  exampleModalContent?: ReactNode;
  logo?: string;
}

/** Converts a manufacturer display name (e.g. "AlphaEss") to its route/config slug (e.g. "alphaess"). */
export const manufacturerToSlug = (manufacturer: string): string => manufacturer.toLowerCase().replace(/\s/g, '-');

const StyledImg = styled.img`
  width: 100%;
  margin: 0 auto;
  display: block;
  margin-top: 1rem;
`;

const StyledImgWithBottomMargin = styled(StyledImg)`
  margin-bottom: 1rem;
`;

const StyledModalSection = styled.div`
  margin-top: 2rem;
`;

const StyledSolisImage = styled.img`
  width: 100%;
`;

const StyledSolisUnorderedList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    position: relative;
    padding-left: 1.5em;
    margin: 0.5em 0;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 0.5em;
      height: 0.5em;
      background-color: ${(props) => props.theme.colors.primary};
      border-radius: 50%;
    }
  }
`;

/**
 * Config for every supported inverter manufacturer, keyed by the slug used in
 * `/onboarding/connection/:manufacturer` routes and the settings "reset connection" modal.
 */
export const inverterManufacturers: Record<string, InverterManufacturerConfig> = {
  alphaess: {
    manufacturer: 'AlphaESS',
    fields: [
      {
        name: 'api_username',
        label: 'Serial number',
        validation: Yup.string()
          .matches(/^[A-Z0-9]{1,20}$/, 'Serial number must be alpha-numeric and up to 20 characters')
          .required('Please enter the serial number'),
      },
      {
        name: 'api_password',
        label: 'Check code',
        type: 'password',
        validation: Yup.string()
          .matches(
            /^[A-Z0-9]{1,8}$/,
            'Check code must be alpha-numeric, letters entered as upper case, and up to 8 characters'
          )
          .required('Please enter the check code'),
      },
    ],
    exampleModalContent: (
      <>
        <p>
          Serial number and check code can be found on a sticker on the system - usually on the right side of the top
          unit. See the example below:
        </p>
        <StyledImg src={serialNumberExample} alt="Serial number example" />
      </>
    ),
  },
  givenergy: {
    manufacturer: 'GivEnergy',
    fields: [
      {
        name: 'api_username',
        label: 'Inverter ID',
        validation: Yup.string()
          .matches(/^[A-Z0-9]{1,20}$/, 'Inverter ID must be alpha-numeric and up to 20 characters')
          .required('Please enter the Inverter ID'),
      },
      {
        name: 'api_password',
        label: 'API token',
        type: 'password',
        validation: Yup.string().required('Please enter the API token'),
      },
    ],
    exampleModalContent: (
      <>
        <div>
          <h4>
            To locate the <strong>Inverter ID</strong>:
          </h4>
          <ul>
            <li>
              Log in to the GivEnergy{' '}
              <a href="https://givenergy.cloud/login" target="_blank" rel="noreferrer">
                website
              </a>
            </li>
            <li>
              Go to <strong>My Inverter</strong>
            </li>
            <li>Inverter ID is at the top of the page:</li>
          </ul>
          <StyledImgWithBottomMargin src={inverterExample} alt="Inverter ID example" />
        </div>

        <StyledModalSection>
          <h4>
            To generate an <strong>API Token</strong>:
          </h4>
          <ul>
            <li>
              Log in to the GivEnergy{' '}
              <a href="https://givenergy.cloud/login" target="_blank" rel="noreferrer">
                website
              </a>
            </li>
            <li>
              Go to <strong>Account Settings</strong>
            </li>
            <li>
              Click on the <strong>MANAGE ACCOUNT SECURITY</strong> button
            </li>
            <li>
              Scroll until you see the <strong>Manage givenergy.cloud API Tokens</strong> section and click on{' '}
              <strong>MANAGE API TOKENS</strong>
            </li>
          </ul>
          <StyledImgWithBottomMargin src={apiTokenExample} alt="API token example" />
        </StyledModalSection>
        <StyledModalSection>
          <ul>
            <li>
              Click on <strong>GENERATE NEW TOKEN</strong>
            </li>
            <li>
              Name the Token something you will recognise. (This is so that you can remember what the token is for in
              case you wish to change or delete it at a later date.)
            </li>
            <li>
              Set the expiry to <strong>No Expiry</strong>. (NOTE: GivEnergy will warn against using{' '}
              <strong>No Expiry</strong>, and will recommend a limit of 90 days. Either will work, but{' '}
              <strong>No Expiry</strong> will mean you won’t have to regenerate a token again in 3 months.)
            </li>
          </ul>
          <StyledImg src={noExpiry} alt="No expiry" />
        </StyledModalSection>
        <StyledModalSection>
          <ul>
            <li>
              Within <strong>Token Scopes</strong>, select{' '}
              <strong>api:inverter: Full control over your inverters</strong>
            </li>
          </ul>
          <StyledImg src={tokenScopes} alt="No expiry" />
          <ul>
            <li>
              Click <strong>CREATE TOKEN</strong>
            </li>
          </ul>
        </StyledModalSection>
      </>
    ),
  },
  growatt: {
    manufacturer: 'Growatt',
    fields: [
      {
        name: 'api_username',
        label: 'Growatt username',
        validation: Yup.string()
          .required('Please enter your Growatt username')
          .max(200, 'Username must be 200 characters or less'),
      },
      {
        name: 'api_password',
        label: 'Growatt password',
        type: 'password',
        validation: Yup.string()
          .required('Please enter your Growatt password')
          .max(200, 'Password must be 200 characters or less'),
      },
    ],
    exampleModalContent: (
      <>
        <div>
          <h4>
            Find your <strong>Username</strong>:
          </h4>
          <ul>
            <li>
              Growatt users will have been given a login by their installer for either an app (ShinePhone) or a{' '}
              <a href="https://server.growatt.com/login" target="_blank" rel="noreferrer">
                website
              </a>
            </li>
            <li>
              If you are logged into the app or the website, you will be able to find your username, and you can choose
              to reset your password
            </li>
            <li>If you’re not logged in and don’t know your username and password, please contact your installer.</li>
            <li>Username location on the website:</li>
          </ul>
          <StyledImgWithBottomMargin src={usernameLocation} alt="Username location on website" />
        </div>

        <StyledModalSection>
          <ul>
            <li>Username location on the app:</li>
          </ul>
          <StyledImgWithBottomMargin src={appUsernameLocation} alt="Username location in app" />
        </StyledModalSection>
      </>
    ),
  },
  instagen: {
    manufacturer: 'Instagen',
    fields: [
      {
        name: 'api_username',
        label: 'Plant name',
        validation: Yup.string()
          .required('Please enter your Plant name')
          .max(200, 'Plant name must be 200 characters or less'),
      },
    ],
    exampleModalContent: (
      <>
        <h4>Your Plant name:</h4>
        <ul>
          <li>
            We don’t yet have details on how to find the <strong>Plant name</strong> for your battery and don’t have an
            example. We assume it is somewhere on the website that Instagen or your installer provided access to.
          </li>
          <li>
            We will update this text as soon as we have more details. For the time being, please ask your installer or
            Instagen to help locate the Plant name.
          </li>
        </ul>
      </>
    ),
  },
  sunsynk: {
    manufacturer: 'Sunsynk',
    fields: [
      {
        name: 'api_username',
        label: 'Sunsynk username',
        validation: Yup.string()
          .required('Please enter your Sunsynk username')
          .max(200, 'Username must be 200 characters or less'),
      },
      {
        name: 'api_password',
        label: 'Sunsynk password',
        type: 'password',
        validation: Yup.string()
          .required('Please enter your Sunsynk password')
          .max(200, 'Password must be 200 characters or less'),
      },
    ],
    exampleModalContent: (
      <div>
        <h3 className="mb-3">Sunsynk</h3>
        <p>This should be the same username and password that you use to log into the Sunsynk website.</p>
      </div>
    ),
  },
  solis: {
    manufacturer: 'Solis',
    fields: [
      {
        name: 'api_username',
        label: 'Solis API Key ID',
        validation: Yup.string()
          .required('Please enter your Solis API Key ID')
          .max(200, 'API Key ID must be 200 characters or less'),
      },
      {
        name: 'api_password',
        label: 'Solis API Key Secret',
        type: 'password',
        validation: Yup.string()
          .required('Please enter your Solis API Key Secret')
          .max(200, 'API Key Secret must be 200 characters or less'),
      },
    ],
    exampleModalContent: (
      <div>
        <h3 className="mb-3">Solis</h3>
        <p className="mb-2">Details Required:</p>
        <StyledSolisUnorderedList>
          <li>Solis API Key ID</li>
          <li>Solis API Key Secret</li>
        </StyledSolisUnorderedList>
        <p>
          In order to get access, you need to submit a ticket to Solis requesting API access{' '}
          <a
            target="_blank"
            rel="noreferrer"
            href="https://solis-service.solisinverters.com/en/support/solutions/articles/44002212561-request-api-access-soliscloud"
          >
            Submit a ticket: Service Centre (solisinverters.com)
          </a>
          . Be sure to select 'API Access Request' and fill in your Solis Cloud email address:
        </p>
        <p>
          <StyledSolisImage src={SolisExampleOne} alt="Solis API access request example" />
        </p>
        <p>Once the ticket is processed, activate in API management under Basic Settings in Solis Cloud:</p>
        <p>
          <StyledSolisImage src={SolisExampleTwo} alt="Solis API management example" />
        </p>
      </div>
    ),
  },
  'mock-inverter': {
    manufacturer: 'Mock',
    fields: [
      {
        name: 'api_username',
        label: 'Mock inverter username',
        validation: Yup.string().required('Please enter your mock inverter username'),
      },
      {
        name: 'api_password',
        label: 'Mock inverter password',
        type: 'password',
        validation: Yup.string().required('Please enter your mock inverter password'),
      },
    ],
  },
};
